import {
  addDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import type { CustomCategory, CustomRoom } from '@easymoving/types';
import { firebaseDb } from '../firebase/init';
import { categoriesCol, categoryDoc, itemDoc, roomDoc, roomsCol } from './paths';
import { toMillis } from './serialize';

/**
 * Items reference a room/category by its value string (default enum key or
 * custom name). So renaming/deleting a custom entry must cascade to every item
 * that uses it — otherwise the item points at a value that no longer exists.
 *
 * The caller passes the affected item ids (it already has the items loaded), so
 * we update them directly in one atomic writeBatch (≤500 ops, fine per user).
 */
async function cascadeTaxonomy(
  uid: string,
  field: 'room' | 'category',
  itemIds: string[],
  action: { type: 'rename'; newValue: string } | { type: 'clear' },
): Promise<void> {
  if (itemIds.length === 0) return;
  const batch = writeBatch(firebaseDb());
  for (const id of itemIds) {
    batch.update(itemDoc(uid, id), {
      [field]: action.type === 'rename' ? action.newValue : deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

/**
 * Only *custom* rooms/categories are persisted (defaults are i18n enum keys).
 * Both collections share the same shape, so one generic pair of helpers covers
 * them.
 */
function entryFromDoc(d: DocumentData, id: string): CustomRoom {
  return {
    id,
    name: d.name ?? '',
    sortOrder: d.sortOrder,
    createdAt: toMillis(d.createdAt),
    updatedAt: toMillis(d.updatedAt),
  };
}

export async function createCustomRoom(uid: string, name: string): Promise<string> {
  const ref = await addDoc(roomsCol(uid), {
    name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}
/** Rename a custom room; cascades the new name to the given items. */
export async function renameCustomRoom(
  uid: string,
  id: string,
  newName: string,
  affectedItemIds: string[],
): Promise<void> {
  await updateDoc(roomDoc(uid, id), { name: newName, updatedAt: serverTimestamp() });
  await cascadeTaxonomy(uid, 'room', affectedItemIds, { type: 'rename', newValue: newName });
}
/** Delete a custom room; clears the room on the given items (items survive). */
export async function deleteCustomRoom(
  uid: string,
  id: string,
  affectedItemIds: string[],
): Promise<void> {
  await cascadeTaxonomy(uid, 'room', affectedItemIds, { type: 'clear' });
  await deleteDoc(roomDoc(uid, id));
}
export function subscribeCustomRooms(uid: string, cb: (rooms: CustomRoom[]) => void): () => void {
  const q = query(roomsCol(uid), orderBy('name', 'asc'));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => entryFromDoc(d.data(), d.id))));
}

export async function createCustomCategory(uid: string, name: string): Promise<string> {
  const ref = await addDoc(categoriesCol(uid), {
    name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}
/** Rename a custom category; cascades the new name to the given items. */
export async function renameCustomCategory(
  uid: string,
  id: string,
  newName: string,
  affectedItemIds: string[],
): Promise<void> {
  await updateDoc(categoryDoc(uid, id), { name: newName, updatedAt: serverTimestamp() });
  await cascadeTaxonomy(uid, 'category', affectedItemIds, { type: 'rename', newValue: newName });
}
/** Delete a custom category; clears the category on the given items (items survive). */
export async function deleteCustomCategory(
  uid: string,
  id: string,
  affectedItemIds: string[],
): Promise<void> {
  await cascadeTaxonomy(uid, 'category', affectedItemIds, { type: 'clear' });
  await deleteDoc(categoryDoc(uid, id));
}
export function subscribeCustomCategories(
  uid: string,
  cb: (categories: CustomCategory[]) => void,
): () => void {
  const q = query(categoriesCol(uid), orderBy('name', 'asc'));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => entryFromDoc(d.data(), d.id))));
}
